import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getTemplates, createTemplate, updateTemplate, deleteTemplate, getQuickLinks, createQuickLink, deleteQuickLink } from '../../api/crm';
import AdminButton from '../../components/admin/AdminButton';

const PresetManagement = () => {
    const [presets, setPresets] = useState([]);
    const [quickLinks, setQuickLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    // Template Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPreset, setEditingPreset] = useState(null);

    // Link Manager Modal
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [linkFormData, setLinkFormData] = useState({ label: '', url: '' });

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        type: 'SCHEDULE',
        content: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [tplData, linkData] = await Promise.all([getTemplates(), getQuickLinks()]);
            setPresets(tplData);
            setQuickLinks(linkData);
        } catch (error) {
            console.error('Failed to load data', error);
            // alert('데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- Template Handlers ---

    const handleOpenModal = (preset = null) => {
        if (preset) {
            setEditingPreset(preset);
            setFormData({ name: preset.name, type: preset.type, content: preset.content || '' });
        } else {
            setEditingPreset(null);
            setFormData({ name: '', type: 'SCHEDULE', content: '' });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPreset(null);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPreset) {
                await updateTemplate(editingPreset.id, formData);
                alert('수정되었습니다.');
            } else {
                await createTemplate(formData);
                alert('생성되었습니다.');
            }
            handleCloseModal();
            fetchData();
        } catch (error) {
            console.error(error);
            alert('저장 중 오류가 발생했습니다.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('정말 삭제하시겠습니까?')) {
            try {
                await deleteTemplate(id);
                fetchData();
            } catch (error) {
                alert('삭제 실패');
            }
        }
    };

    // --- Link Handlers ---

    const handleLinkSubmit = async (e) => {
        e.preventDefault();
        try {
            await createQuickLink(linkFormData);
            setLinkFormData({ label: '', url: '' });
            fetchData(); // Refresh list
        } catch (error) {
            alert('링크 생성 실패');
        }
    };

    const handleDeleteLink = async (id) => {
        if (window.confirm('링크를 삭제하시겠습니까?')) {
            try {
                await deleteQuickLink(id);
                fetchData();
            } catch (error) {
                alert('삭제 실패');
            }
        }
    };


    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">메일 템플릿 관리</h1>
                    <div className="flex gap-3">
                        <AdminButton variant="secondary" onClick={() => setIsLinkModalOpen(true)}>
                            🔗 링크 관리
                        </AdminButton>
                        <AdminButton variant="primary" onClick={() => handleOpenModal()}>
                            + 새 템플릿 추가
                        </AdminButton>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {presets.map(preset => (
                            <div key={preset.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col hover:shadow-md transition-shadow">
                                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {preset.type}
                                    </span>
                                </div>
                                <div className="p-6 flex-1 flex flex-col">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate">{preset.name}</h3>
                                    <div className="text-sm text-gray-600 mb-6 flex-1 whitespace-pre-wrap line-clamp-4">
                                        {preset.content}
                                    </div>
                                    <div className="flex justify-end gap-3 mt-auto pt-4 border-t border-gray-100">
                                        <AdminButton variant="secondary" size="sm" onClick={() => handleOpenModal(preset)}>수정</AdminButton>
                                        <AdminButton variant="danger" size="sm" onClick={() => handleDelete(preset.id)}>삭제</AdminButton>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Template Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleCloseModal}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                {editingPreset ? '템플릿 수정' : '새 템플릿 생성'}
                                            </h3>
                                            <div className="mt-4">
                                                <form onSubmit={handleSubmit} className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">템플릿 이름</label>
                                                        <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="예: 기본 일정 안내" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">유형 (Action Type)</label>
                                                        <select name="type" value={formData.type} onChange={handleChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border bg-white">
                                                            <option value="SCHEDULE">SCHEDULE (일정 안내)</option>
                                                            <option value="PROPOSAL">PROPOSAL (제안서 발송)</option>
                                                            <option value="ESTIMATE">ESTIMATE (견적서 발송)</option>
                                                            <option value="MEETING">MEETING (미팅/방문 제안)</option>
                                                            <option value="REJECT">REJECT (거절/보류)</option>
                                                            <option value="GENERAL">GENERAL (일반/기타)</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">내용 (Placeholder: &#123;customerName&#125;, &#123;companyName&#125;)</label>
                                                        <textarea name="content" value={formData.content} onChange={handleChange} required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border h-32"></textarea>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 gap-2">
                                    <AdminButton type="button" variant="primary" onClick={handleSubmit}>
                                        저장
                                    </AdminButton>
                                    <AdminButton type="button" variant="secondary" onClick={handleCloseModal}>
                                        취소
                                    </AdminButton>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Link Manager Modal */}
                {isLinkModalOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setIsLinkModalOpen(false)}></div>
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-1">자주 쓰는 링크 관리</h3>
                                    <p className="text-sm text-gray-500 mb-5">메일 작성 시 삽입할 수 있는 링크입니다.</p>

                                    <form onSubmit={handleLinkSubmit} className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-100">
                                        <div className="flex flex-col sm:flex-row gap-3 mb-3">
                                            <input
                                                type="text"
                                                placeholder="라벨 (예: Zoom)"
                                                value={linkFormData.label}
                                                onChange={e => setLinkFormData({ ...linkFormData, label: e.target.value })}
                                                required
                                                className="flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border bg-white"
                                            />
                                            <input
                                                type="url"
                                                placeholder="URL (https://...)"
                                                value={linkFormData.url}
                                                onChange={e => setLinkFormData({ ...linkFormData, url: e.target.value })}
                                                required
                                                className="flex-[2] block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border bg-white"
                                            />
                                        </div>
                                        <AdminButton type="submit" variant="primary" fullWidth>
                                            추가하기
                                        </AdminButton>
                                    </form>

                                    <div className="max-h-64 overflow-y-auto pr-2 space-y-2">
                                        {quickLinks.map(link => (
                                            <div key={link.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                                                <div className="min-w-0 flex-1 mr-4">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{link.label}</p>
                                                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 truncate block mt-0.5">{link.url}</a>
                                                </div>
                                                <button onClick={() => handleDeleteLink(link.id)} className="inline-flex items-center p-1.5 border border-transparent rounded-md text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors">
                                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                        {quickLinks.length === 0 && (
                                            <div className="text-center py-8 text-gray-500 text-sm">등록된 링크가 없습니다.</div>
                                        )}
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
                                    <AdminButton type="button" variant="secondary" onClick={() => setIsLinkModalOpen(false)}>
                                        닫기
                                    </AdminButton>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default PresetManagement;

